import { Button, Checkbox, Field, Input, RadioGroup, Select, Skeleton } from '@bb/ui';
import { useRef, useState, type ReactNode } from 'react';
import { NumberInput } from '../../campaigns/trigger/NumberInput.tsx';
import { toSeconds, UNIT_SECONDS, unitOf, type DelayUnit } from '../../campaigns/trigger/actionBased.ts';
import { PersonalizationButton } from '../shared/PersonalizationButton.tsx';
import { KeyValuePairsEditor } from './KeyValuePairsEditor.tsx';
import { COLLAPSE_KEY_MAX_BYTES, formatDuration, hasLiquid, utf8Bytes, type IssueLookup } from './pushChecks.ts';
import { setSetting, type PushContentInput, type PushPlatform, type PushSettingsInput } from './pushDraft.ts';
import { errorText, FieldIssueList } from './PushIssues.tsx';
import type { PushRegistry } from './usePushRegistry.ts';

export interface PushSettingsTabProps {
  environmentId: string;
  /** The campaign being edited: its trigger data is offered by the personalization buttons. */
  campaignId?: string;
  /** A transactional (trading) message gets the recommended values as hints. */
  category: 'marketing' | 'transactional';
  /** The content whose `settings` and `extras` are edited. */
  value: PushContentInput;
  onChange: (next: PushContentInput) => void;
  /** Platforms the content is written for: the Android and iOS sections follow them. */
  platforms: readonly PushPlatform[];
  registry: PushRegistry;
  issues: IssueLookup;
  readOnly?: boolean;
}

/** Channel coverage below this share of installations is called out. */
export const CHANNEL_COVERAGE_WARNING_PCT = 90;
const MAX_TTL_SECONDS = 2_419_200;
const INT32 = { min: -2_147_483_648, max: 2_147_483_647 };
const TTL_UNITS: Array<{ value: DelayUnit; label: string }> = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];

type Settings = PushSettingsInput;
type SetSetting = <K extends keyof Settings>(key: K, value: Settings[K] | undefined) => void;

function Recommended({ show, children }: { show: boolean; children: ReactNode }) {
  return show ? <p className="hint push-recommended">Recommended for trading alerts: {children}</p> : null;
}

function TtlField({ settings, set, registry, transactional, readOnly, issues }: { settings: Settings; set: SetSetting; registry: PushRegistry; transactional: boolean; readOnly: boolean; issues: IssueLookup }) {
  const ttl = settings.ttl ?? { mode: 'environment_default' as const };
  const defaults = registry.settings.data;
  const [unit, setUnit] = useState<DelayUnit>(() => (ttl.mode === 'custom' ? unitOf(ttl.seconds) : 'hours'));
  const [empty, setEmpty] = useState(false);
  const defaultLabel = defaults ? `Environment default (${formatDuration(defaults.defaultTtlSeconds)})` : 'Environment default';
  const seconds = ttl.mode === 'custom' ? ttl.seconds : undefined;
  const tooLong = seconds !== undefined && seconds > MAX_TTL_SECONDS;
  const emit = (amount: number | undefined, u: DelayUnit) => {
    setEmpty(amount === undefined);
    if (amount !== undefined && amount >= 0) set('ttl', { mode: 'custom', seconds: toSeconds(amount, u) });
  };
  return (
    <div className="push-setting">
      <Field label="Time to live" hint="How long providers keep trying to deliver to an offline device; 0 — now or never. iOS expires at the same time." error={errorText(issues('settings.ttl', { prefix: true }))}>
        <Select
          options={[
            { value: 'environment_default', label: defaultLabel },
            { value: 'custom', label: 'Custom' },
          ]}
          value={ttl.mode}
          disabled={readOnly}
          onChange={(e) => {
            if (e.target.value === 'environment_default') return set('ttl', { mode: 'environment_default' });
            const start = transactional ? UNIT_SECONDS.hours : (defaults?.defaultTtlSeconds ?? MAX_TTL_SECONDS);
            setUnit(unitOf(start));
            set('ttl', { mode: 'custom', seconds: start });
          }}
        />
      </Field>
      {registry.settings.isError && <p className="hint">The environment default could not be loaded.</p>}
      {ttl.mode === 'custom' && (
        <div className="audience-filter">
          <Field label="Time to live amount" error={empty ? 'Enter a duration.' : tooLong ? 'At most 28 days.' : undefined}>
            <NumberInput min={0} step={1} value={seconds === undefined ? undefined : seconds / UNIT_SECONDS[unit]} disabled={readOnly} onValueChange={(n) => emit(n, unit)} />
          </Field>
          <Field label="Time to live unit">
            <Select
              options={TTL_UNITS}
              value={unit}
              disabled={readOnly}
              onChange={(e) => {
                const next = e.target.value as DelayUnit;
                setUnit(next);
                if (seconds !== undefined) emit(seconds / UNIT_SECONDS[unit], next);
              }}
            />
          </Field>
        </div>
      )}
      <Recommended show={transactional}>1 hour or less — a late signal does harm.</Recommended>
    </div>
  );
}

function CollapseKeyField({ environmentId, campaignId, settings, set, transactional, readOnly, issues }: { environmentId: string; campaignId?: string; settings: Settings; set: SetSetting; transactional: boolean; readOnly: boolean; issues: IssueLookup }) {
  const ref = useRef<HTMLInputElement>(null);
  const key = settings.collapseKey ?? '';
  const bytes = utf8Bytes(key);
  const templated = hasLiquid(key);
  const local = !templated && bytes > COLLAPSE_KEY_MAX_BYTES ? `${bytes} bytes: at most ${COLLAPSE_KEY_MAX_BYTES}.` : undefined;
  const found = issues('settings.collapseKey');
  const write = (text: string) => set('collapseKey', text === '' ? undefined : text);
  return (
    <div className="push-setting">
      <div className="push-text-field__row">
        <Field label="Collapse key" hint="Optional: a newer push with the same key replaces an undelivered or shown one (FCM collapse key, APNs collapse id); may use Liquid." error={local ?? errorText(found)}>
          <Input ref={ref} value={key} spellCheck={false} disabled={readOnly} onChange={(e) => write(e.target.value)} />
        </Field>
        <PersonalizationButton environmentId={environmentId} campaignId={campaignId} target={ref} value={key} onChange={write} label="Personalize collapse key" disabled={readOnly} />
      </div>
      <p className="hint push-bytes" aria-label="Collapse key size">
        {templated ? `${bytes} bytes as written; checked after rendering: at most ${COLLAPSE_KEY_MAX_BYTES} bytes.` : `${bytes} of ${COLLAPSE_KEY_MAX_BYTES} bytes`}
      </p>
      <Recommended show={transactional}>one key per position or instrument, e.g. {'{{event_properties.${symbol}}}'}.</Recommended>
    </div>
  );
}

function AndroidSection({ environmentId, campaignId, value, settings, set, registry, transactional, readOnly, issues }: { environmentId: string; campaignId?: string; value: PushContentInput; settings: Settings; set: SetSetting; registry: PushRegistry; transactional: boolean; readOnly: boolean; issues: IssueLookup }) {
  const defaults = registry.settings.data;
  const priority = settings.androidPriority ?? 'environment_default';
  const channels = registry.channels.data ?? [];
  const active = channels.filter((c) => !c.archivedAt);
  const fallback = active.find((c) => c.isFallback);
  const current = settings.androidChannelId;
  const known = current === undefined || active.some((c) => c.channelId === current);
  const chosen = active.find((c) => c.channelId === (current ?? fallback?.channelId));
  const pct = chosen?.seenOnInstallationsPct;
  const nid = settings.androidNotificationId;
  const nidMode = !nid ? 'none' : 'fixed' in nid ? 'fixed' : 'template';
  const nidRef = useRef<HTMLInputElement>(null);
  const nidTemplate = nid && 'template' in nid ? nid.template : '';
  const fixedError = nid && 'fixed' in nid && (!Number.isInteger(nid.fixed) || nid.fixed < INT32.min || nid.fixed > INT32.max) ? 'A whole number that fits 32 bits.' : undefined;
  const [freeChannel, setFreeChannel] = useState(false);
  const priorityDefault = transactional ? 'high for transactional messages' : (defaults?.defaultAndroidPriority ?? 'normal');

  return (
    <fieldset className="push-settings__platform" aria-label="Android settings">
      <legend>Android</legend>
      <div className="push-setting">
        <Field label="Delivery priority" error={errorText({ errors: issues('settings.androidPriority').errors, warnings: [] })}>
          <Select
            options={[
              { value: 'environment_default', label: `Environment default (${priorityDefault})` },
              { value: 'normal', label: 'Normal' },
              { value: 'high', label: 'High' },
            ]}
            value={priority}
            disabled={readOnly}
            onChange={(e) => set('androidPriority', e.target.value as Settings['androidPriority'])}
          />
        </Field>
        {value.notificationType === 'silent' && <p className="hint">A silent push always goes with normal priority.</p>}
        {!transactional && priority === 'high' && <p className="hint push-issue--warning">High priority wakes the device: FCM may lower the app’s priority when such pushes show nothing important.</p>}
        <FieldIssueList issues={{ errors: [], warnings: issues('settings.androidPriority').warnings.filter((w) => w.code !== 'high_priority_marketing') }} />
        <Recommended show={transactional}>High.</Recommended>
      </div>

      <div className="push-setting">
        {registry.channels.isPending ? (
          <Skeleton label="Loading notification channels" />
        ) : registry.channels.isError || freeChannel ? (
          <Field label="Notification channel" hint="Channel ID as the app registers it; empty — the environment fallback channel." error={errorText({ errors: issues('settings.androidChannelId').errors, warnings: [] })}>
            <Input value={current ?? ''} spellCheck={false} disabled={readOnly} onChange={(e) => set('androidChannelId', e.target.value.trim() === '' ? undefined : e.target.value.trim())} />
          </Field>
        ) : (
          <Field label="Notification channel" hint="The Android channel the notification is shown in (Push settings → Android channels)." error={errorText({ errors: issues('settings.androidChannelId').errors, warnings: [] })}>
            <Select
              options={[
                { value: '', label: `Environment fallback${fallback ? ` (${fallback.channelId})` : ''}` },
                ...active.map((c) => ({ value: c.channelId, label: `${c.name} (${c.channelId})` })),
                ...(!known && current ? [{ value: current, label: `${current} (not in the registry)` }] : []),
              ]}
              value={current ?? ''}
              disabled={readOnly}
              onChange={(e) => set('androidChannelId', e.target.value === '' ? undefined : e.target.value)}
            />
          </Field>
        )}
        {registry.channels.isError && <p className="notice notice--warning">The channel registry could not be loaded: type the channel ID.</p>}
        {registry.channels.isSuccess && !freeChannel && !readOnly && (
          <Button size="sm" variant="ghost" onClick={() => setFreeChannel(true)}>
            Type a channel ID instead
          </Button>
        )}
        {!known && current && <p className="hint push-issue--warning">“{current}” is not in the channel registry: devices without it show the notification in the fallback channel.</p>}
        {registry.channels.isSuccess && chosen && (pct === null || pct === undefined) && <p className="hint push-coverage">Coverage unknown: apps do not report their channels yet.</p>}
        {registry.channels.isSuccess && chosen && typeof pct === 'number' && (
          <p className={`hint push-coverage${pct < CHANNEL_COVERAGE_WARNING_PCT ? ' push-issue--warning' : ''}`}>
            {pct < CHANNEL_COVERAGE_WARNING_PCT
              ? `“${chosen.channelId}” is seen on ${pct}% of Android installations: the others show the notification in the fallback channel.`
              : `“${chosen.channelId}” is seen on ${pct}% of Android installations.`}
          </p>
        )}
        <FieldIssueList issues={{ errors: [], warnings: issues('settings.androidChannelId').warnings.filter((w) => w.code !== 'unknown_channel' || known) }} />
        <Recommended show={transactional}>the trading_alerts channel.</Recommended>
      </div>

      <div className="push-setting">
        <Field label="Notification ID" hint="Optional: a push with the same ID replaces the notification shown.">
          <Select
            options={[
              { value: 'none', label: 'None' },
              { value: 'fixed', label: 'Fixed number' },
              { value: 'template', label: 'From a template' },
            ]}
            value={nidMode}
            disabled={readOnly}
            onChange={(e) => set('androidNotificationId', e.target.value === 'fixed' ? { fixed: 1 } : e.target.value === 'template' ? { template: '{{event_properties.${symbol}}}' } : undefined)}
          />
        </Field>
        {nid && 'fixed' in nid && (
          <Field label="Notification ID number" error={fixedError ?? errorText(issues('settings.androidNotificationId', { prefix: true }))}>
            <NumberInput step={1} value={nid.fixed} disabled={readOnly} onValueChange={(n) => n !== undefined && set('androidNotificationId', { fixed: n })} />
          </Field>
        )}
        {nidMode === 'template' && (
          <div className="push-text-field__row">
            <Field label="Notification ID template" hint="Rendered for each user and hashed (FNV-1a) into a number: the same text replaces the same notification." error={errorText(issues('settings.androidNotificationId', { prefix: true }))}>
              <Input ref={nidRef} value={nidTemplate} spellCheck={false} disabled={readOnly} onChange={(e) => set('androidNotificationId', { template: e.target.value })} />
            </Field>
            <PersonalizationButton environmentId={environmentId} campaignId={campaignId} target={nidRef} value={nidTemplate} onChange={(t) => set('androidNotificationId', { template: t })} label="Personalize notification ID" disabled={readOnly} />
          </div>
        )}
      </div>
    </fieldset>
  );
}

function IosSection({ environmentId, campaignId, settings, set, registry, transactional, readOnly, issues }: { environmentId: string; campaignId?: string; settings: Settings; set: SetSetting; registry: PushRegistry; transactional: boolean; readOnly: boolean; issues: IssueLookup }) {
  const defaults = registry.settings.data;
  const level = settings.iosInterruptionLevel ?? 'environment_default';
  const available = registry.timeSensitiveAvailable;
  const threads = (registry.threads.data ?? []).filter((t) => !t.archivedAt);
  const thread = settings.iosThreadId;
  const [typingThread, setTypingThread] = useState(false);
  const customThread = typingThread || (thread !== undefined && !threads.some((t) => t.threadId === thread));
  const badge = settings.iosBadge ?? { mode: 'none' as const };
  const badgeRef = useRef<HTMLInputElement>(null);
  const relevance = settings.iosRelevanceScore;
  const relevanceError = relevance !== undefined && (relevance < 0 || relevance > 1) ? 'Between 0 and 1.' : undefined;
  const levelWarnings = issues('settings.iosInterruptionLevel').warnings.filter((w) => w.code !== 'time_sensitive_not_enabled');

  return (
    <fieldset className="push-settings__platform" aria-label="iOS settings">
      <legend>iOS</legend>
      <RadioGroup
        label="iOS devices"
        options={[
          { value: 'all', label: 'iPhone and iPad', disabled: readOnly },
          { value: 'iphone', label: 'iPhones only', disabled: readOnly },
          { value: 'ipad', label: 'iPads only', disabled: readOnly },
        ]}
        value={settings.iosDeviceFamily ?? 'all'}
        onValueChange={(v) => set('iosDeviceFamily', v as Settings['iosDeviceFamily'])}
      />

      <div className="push-setting">
        <Field
          label="Interruption level"
          hint={
            available === true
              ? 'Time Sensitive breaks through Focus; use it for alerts users asked for.'
              : `Time Sensitive needs an iOS app with the time-sensitive entitlement (Settings → Apps → push capabilities)${available === false ? '; no iOS app of the environment declares it' : ''}.`
          }
          error={errorText({ errors: issues('settings.iosInterruptionLevel').errors, warnings: [] })}
        >
          <Select
            options={[
              { value: 'environment_default', label: `Environment default (${defaults?.defaultIosInterruptionLevel ?? 'active'})` },
              { value: 'passive', label: 'Passive' },
              { value: 'active', label: 'Active' },
              { value: 'time-sensitive', label: 'Time Sensitive', disabled: available !== true && level !== 'time-sensitive' },
            ]}
            value={level}
            disabled={readOnly}
            onChange={(e) => set('iosInterruptionLevel', e.target.value as Settings['iosInterruptionLevel'])}
          />
        </Field>
        {available === false && level === 'time-sensitive' && <p className="hint push-issue--warning">Sent as Active: no iOS app declares the time-sensitive entitlement.</p>}
        <FieldIssueList issues={{ errors: [], warnings: levelWarnings }} />
        <Recommended show={transactional && available === true}>Time Sensitive.</Recommended>
      </div>

      <Field label="Relevance score" hint="Optional, 0–1: how iOS ranks the notification in the notification summary." error={relevanceError ?? errorText(issues('settings.iosRelevanceScore'))}>
        <NumberInput min={0} max={1} step={0.1} value={relevance} disabled={readOnly} onValueChange={(n) => set('iosRelevanceScore', n)} />
      </Field>

      <div className="push-setting">
        <Field label="Thread" hint="Optional: notifications of one thread are grouped (Push settings → iOS threads).">
          <Select
            options={[{ value: '', label: 'None' }, ...threads.map((t) => ({ value: t.threadId, label: `${t.label} (${t.threadId})` })), { value: '__custom', label: 'Other…' }]}
            value={customThread ? '__custom' : (thread ?? '')}
            disabled={readOnly}
            onChange={(e) => {
              setTypingThread(e.target.value === '__custom');
              if (e.target.value !== '__custom') set('iosThreadId', e.target.value === '' ? undefined : e.target.value);
            }}
          />
        </Field>
        {customThread && (
          <Field label="Thread ID" error={errorText(issues('settings.iosThreadId'))}>
            <Input value={thread ?? ''} maxLength={64} spellCheck={false} disabled={readOnly} onChange={(e) => set('iosThreadId', e.target.value === '' ? undefined : e.target.value)} />
          </Field>
        )}
        {registry.threads.isError && <p className="hint">The thread registry could not be loaded: choose Other… to type a thread ID.</p>}
      </div>

      <div className="push-setting">
        <Field label="Badge">
          <Select
            options={[
              { value: 'none', label: 'Leave as it is' },
              { value: 'set', label: 'Set to' },
              { value: 'clear', label: 'Clear' },
            ]}
            value={badge.mode}
            disabled={readOnly}
            onChange={(e) => set('iosBadge', e.target.value === 'set' ? { mode: 'set', value: '1' } : { mode: e.target.value as 'none' | 'clear' })}
          />
        </Field>
        {badge.mode === 'set' && (
          <div className="push-text-field__row">
            <Field label="Badge number" hint="A whole number ≥ 0 after rendering, e.g. {{custom_attribute.${unread_count}}}; otherwise the push is skipped." error={errorText(issues('settings.iosBadge', { prefix: true }))}>
              <Input ref={badgeRef} value={badge.value} spellCheck={false} disabled={readOnly} onChange={(e) => set('iosBadge', { mode: 'set', value: e.target.value })} />
            </Field>
            <PersonalizationButton environmentId={environmentId} campaignId={campaignId} target={badgeRef} value={badge.value} onChange={(v) => set('iosBadge', { mode: 'set', value: v })} label="Personalize badge" disabled={readOnly} />
          </div>
        )}
      </div>

      <Field label="Sound">
        <Select
          options={[
            { value: 'environment_default', label: `Environment default (${defaults?.defaultSound ?? 'default'})` },
            { value: 'default', label: 'Default sound' },
            { value: 'none', label: 'No sound' },
          ]}
          value={settings.sound ?? 'environment_default'}
          disabled={readOnly}
          onChange={(e) => set('sound', e.target.value as Settings['sound'])}
        />
      </Field>
    </fieldset>
  );
}

/**
 * The Settings tab of the push composer (BB-76): key-value pairs for the app, which devices get the push, how long it
 * may wait, the collapse key, and per platform — Android priority, channel (with how many installations have it) and
 * notification id; iOS devices, interruption level (Time Sensitive only with an entitled app), relevance, thread,
 * badge and sound. Unset values follow the environment defaults (Push settings, BB-77), named in each select. A
 * transactional message shows the recommended values for trading alerts.
 */
export function PushSettingsTab({ environmentId, campaignId, category, value, onChange, platforms, registry, issues, readOnly = false }: PushSettingsTabProps) {
  const settings = value.settings ?? {};
  const set: SetSetting = (key, next) => onChange(setSetting(value, key, next));
  const transactional = category === 'transactional';
  const apps = registry.appsOf(platforms);
  const chosenApps = settings.appInstanceIds ?? [];
  const toggleApp = (id: string, on: boolean) => {
    const next = on ? [...chosenApps, id] : chosenApps.filter((a) => a !== id);
    set('appInstanceIds', next.length ? next : undefined);
  };
  const shared = { environmentId, campaignId, settings, set, registry, transactional, readOnly, issues };

  return (
    <div className="stack-form push-settings" role="group" aria-label="Send settings">
      {transactional && (
        <p className="notice notice--neutral">
          Transactional push: trading alerts should go with high priority, live 1 hour or less, carry a collapse key per position or instrument, use the trading_alerts channel and, on iOS, Time Sensitive.
        </p>
      )}
      {registry.settings.isPending && <Skeleton label="Loading push defaults" />}
      <KeyValuePairsEditor
        value={value.extras ?? {}}
        readOnly={readOnly}
        issues={issues}
        onChange={(extras) => onChange({ ...value, extras })}
      />
      <fieldset className="push-settings__delivery" aria-label="Delivery">
        <legend>Delivery</legend>
        <RadioGroup
          label="Push destination"
          options={[
            { value: 'all_devices', label: 'All devices with a push token', disabled: readOnly },
            { value: 'most_recent_device', label: 'Only the most recently used device', disabled: readOnly },
          ]}
          value={settings.destination ?? 'all_devices'}
          onValueChange={(v) => set('destination', v as Settings['destination'])}
        />
        {apps.length > 0 && (
          <fieldset className="push-apps">
            <legend>Apps</legend>
            <p className="hint">None selected: every app of the platform{platforms.length > 1 ? 's' : ''}.</p>
            {apps.map((a) => (
              <Checkbox key={a.id} label={`${a.name} (${a.platform === 'ios' ? 'iOS' : 'Android'})`} checked={chosenApps.includes(a.id)} disabled={readOnly} onCheckedChange={(on) => toggleApp(a.id, on)} />
            ))}
          </fieldset>
        )}
        <TtlField {...shared} />
        <CollapseKeyField {...shared} />
      </fieldset>
      {platforms.includes('android') && <AndroidSection {...shared} value={value} />}
      {platforms.includes('ios') && <IosSection {...shared} />}
      <FieldIssueList issues={issues('settings.appInstanceIds', { prefix: true })} />
    </div>
  );
}
